// CsvUploadArea.tsx
import React, { useRef, useState } from "react";
import Papa from "papaparse";
import ExcelJS from "exceljs";
import { Plus } from "lucide-react";
import { useUser } from "@/utils/userContext/UserContext";
import styles from "@/pages/Dashboard/Clientes/Clientes.module.css";
import api from "@/utils/connector/ApiConnector";
import { CustomerDto } from "../dto/ImportCoworkingCustomerRow";
import { documentHelper } from "@/utils/common/documentHelper";
import { phoneNumberHelper } from "@/utils/common/phoneNumberHelper";
import { useInvalidateCache } from "@/utils/hooks/useInvalidateCache";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import ImportPreviewModal from "./ImportPreviewModal";
import ImportErrorModal from "./ImportErrorModal";
import { validateImportedCustomerRow } from "../common/importCustomerValidation";

const importFieldLabels: Record<string, string> = {
  email: "email",
  phone: "telefone",
  productName: "produto",
  startDate: "data de início",
  endDate: "data de término",
  affiliateId: "afiliado",
};

function formatImportErrorMessage(details: Array<{ row: number; field?: string | null; message: string }>) {
  return details
    .slice(0, 5)
    .map((detail) => {
      const fieldLabel = detail.field ? importFieldLabels[detail.field] ?? detail.field : null;
      const fieldSuffix = fieldLabel ? ` (${fieldLabel})` : "";
      return `Linha ${detail.row}${fieldSuffix}: ${detail.message}`;
    })
    .join("\n");
}


const CsvUploadArea: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<CustomerDto[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState<any[]>([]);
  const userContext = useUser();
  const userData = userContext?.userData;
  const { invalidateClients } = useInvalidateCache();
  const modal = useGlobalModal();

  const handleFileUpload = () => {
    // Reset file input before opening to ensure onChange fires even for same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await parseFile(file);
    }
    // Always reset the input value
    if (event.currentTarget) {
      event.currentTarget.value = "";
    }
  };

  // Step 1: Parse the file and show preview modal
  async function parseFile(file: File) {
    try {
      setParsing(true);
      setFileName(file.name);
      
      const fileNameLower = file.name.toLowerCase();
      const isXlsx = fileNameLower.endsWith('.xlsx') || fileNameLower.endsWith('.xls');
      
      let parsedRows: Record<string, string>[];
      
      if (isXlsx) {
        parsedRows = await convertXlsxToJsonList(file);
      } else {
        parsedRows = await convertCsvToJsonList(file);
      }
      
      if (!parsedRows || parsedRows.length === 0) {
        modal.show({
          type: "error",
          title: "Erro ao processar arquivo",
          message: "Verifique se o arquivo está correto e contém dados.",
          autoClose: 4500,
        });
        return;
      }

      const customers = mapRowsToCustomerDtos(parsedRows);
      
      // Show preview modal instead of immediately submitting
      setPreviewData(customers);
      setPreviewOpen(true);
      
    } catch (error: any) {
      console.error("parseFile error:", error);
      modal.show({
        type: "error",
        title: "Erro ao processar arquivo",
        message: "Verifique se o formato está correto.",
        autoClose: 4500,
      });
    } finally {
      setParsing(false);
    }
  }

  // Step 2: Submit confirmed data to API
  async function submitImport() {
    try {
      setLoading(true);
      
      // Filter out invalid rows (required fields, phone and date format)
      const validCustomers = previewData.filter((customer) => {
        const validation = validateImportedCustomerRow(customer);
        return validation.fields.length === 0;
      });

      const skippedCount = previewData.length - validCustomers.length;

      if (validCustomers.length === 0) {
        modal.show({
          type: "error",
          title: "Importação inválida",
          message: "Nenhuma linha válida para importar. Verifique campos obrigatórios, telefone (10 ou 11 dígitos) e datas no formato YYYY-MM-DD.",
          autoClose: 5000,
        });
        return;
      }

      if (skippedCount > 0) {
        const proceed = window.confirm(
          `${skippedCount} linha(s) serão ignoradas por erro de validação (campos obrigatórios, telefone ou data).\n\n` +
          `Deseja continuar com a importação de ${validCustomers.length} cliente(s)?`
        );
        if (!proceed) return;
      }

      const token = userData?.auth;
      type PostCustomerResponse = { 
        subscriptionsCount?: number;
        customers?: { id: string }[];
        message?: string;
        details?: { row: number; field?: string; message: string }[];
        created?: { id: string; email: string }[];
      };
      const data = await api.post<PostCustomerResponse>('/customer', { customers: validCustomers }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      // Considera sucesso se não lançar erro e o payload contém subscriptionsCount
      if (data?.subscriptionsCount && data.subscriptionsCount >= 1) {
        await invalidateClients();
        modal.show({
          type: "success",
          title: "Importação concluída",
          message: `${data.subscriptionsCount} assinatura(s) criada(s) com sucesso.`,
          autoClose: 3500,
        });
        handleClosePreview();
      } else if (data?.customers && data.customers.length > 0) {
        await invalidateClients();
        modal.show({
          type: "success",
          title: "Importação concluída",
          message: `${data.customers.length} cliente(s) criado(s).`,
          autoClose: 3500,
        });
        handleClosePreview();
      } else {
        modal.show({
          type: "error",
          title: "Import failed",
          message: "Verifique se os produtos existem no sistema.",
          autoClose: 4500,
        });
      }
    } catch (error: any) {
      console.error("submitImport error:", error);
      
      // Try to extract detailed error message from backend
      const errorData = error?.response?.data;
      if (errorData?.details && Array.isArray(errorData.details)) {
        setErrorDetails(errorData.details);
        setErrorModalOpen(true);
      } else if (errorData?.message) {
        modal.show({
          type: "error",
          title: "Erro",
          message: String(errorData.message),
          autoClose: 5000,
        });
      } else {
        modal.show({
          type: "error",
          title: "Erro ao importar",
          message: "Verifique os dados e tente novamente.",
          autoClose: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  function handleClosePreview() {
    setPreviewOpen(false);
    setPreviewData([]);
    setFileName("");
    // Reset file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function handleCloseErrorModal() {
    setErrorModalOpen(false);
    setErrorDetails([]);
  }

  async function convertXlsxToJsonList(file: File): Promise<Record<string, string>[]> {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    // Get the first worksheet (Clientes)
    const worksheet = workbook.worksheets[0];
    if (!worksheet || worksheet.rowCount < 2) {
      return [];
    }
    
    // Get headers from row 1
    const headerRow = worksheet.getRow(1);
    const headers: string[] = [];
    headerRow.eachCell((cell, colNumber) => {
      const value = cell.value?.toString() || '';
      headers[colNumber - 1] = cleanHeader(value);
    });
    
    const rows: Record<string, string>[] = [];
    
    // Iterate through data rows (starting from row 2)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row
      
      const rowData: Record<string, string> = {};
      let hasData = false;
      
      row.eachCell((cell, colNumber) => {
        const header = headers[colNumber - 1];
        if (!header) return;
        
        let value = '';
        if (cell.value !== null && cell.value !== undefined) {
          if (cell.value instanceof Date) {
            // Format date as YYYY-MM-DD
            const d = cell.value;
            value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          } else if (typeof cell.value === 'object' && 'richText' in cell.value) {
            // Handle rich text
            value = (cell.value as any).richText.map((rt: any) => rt.text).join('');
          } else {
            value = cell.value.toString();
          }
        }
        
        rowData[header] = value.trim();
        if (value.trim()) hasData = true;
      });
      
      // Skip rows that look like description rows (contain "(obrigatório)" or similar patterns)
      const firstValue = Object.values(rowData)[0] || '';
      const isDescriptionRow = firstValue.includes('(obrigatório)') || 
                               firstValue.includes('(opcional)') ||
                               firstValue.toLowerCase().includes('nome empresarial') ||
                               firstValue.toLowerCase().includes('email (obrigatório');
      
      if (hasData && !isDescriptionRow) {
        rows.push(rowData);
      }
    });
    
    console.log("PARSED XLSX rows (sample):", rows.slice(0, 5));
    return rows;
  }

 async function convertCsvToJsonList(file: File): Promise<any[]> {
  const text = await readFileAsText(file);
  const cleanedText = text.replace(/\uFEFF/g, ""); // remove BOM se existir

  let delimiter = ",";
  if (cleanedText.indexOf("\t") >= 0 && cleanedText.indexOf(",") === -1) {
    delimiter = "\t";
  } else if (cleanedText.indexOf(";") >= 0 && cleanedText.indexOf(",") === -1) {
    delimiter = ";";
  } else {
    const firstLine = cleanedText.split(/\r?\n/)[0] || "";
    const counts = { ",": (firstLine.match(/,/g) || []).length, ";": (firstLine.match(/;/g) || []).length, "\t": (firstLine.match(/\t/g) || []).length };
    delimiter = Object.entries(counts).sort((a,b)=> (b[1]-a[1]))[0][0] as string;
  }

  return new Promise((resolve, reject) => {
    Papa.parse(cleanedText, {
      header: true,
      skipEmptyLines: true,
      delimiter,
      transformHeader: (h: string): string => cleanHeader(h),
      dynamicTyping: false,
      complete: (results: Papa.ParseResult<Record<string, string>>) => {
      if (results.errors && results.errors.length) {
        console.warn("PapaParse errors:", results.errors);
      }

      const rows: Record<string, string>[] = (results.data as Record<string, any>[])
        .map((raw: Record<string, any>): Record<string, string> => {
        const row: Record<string, string> = {};
        Object.entries(raw).forEach(([k, v]: [string, any]) => {
          row[k] = (v ?? "").toString().trim();
        });
        return row;
        })
        .filter((r: Record<string, string>): boolean => Object.values(r).some((v: string): boolean => String(v || "").trim() !== ""));

      console.log("PARSED CSV rows (sample):", rows.slice(0, 5));
      resolve(rows);
      },
      error: (err: Error) => {
      console.error("PapaParse fatal error:", err);
      reject(err);
      }
    });
  });
}

function toISODateFromPossibleFormats(s: string | undefined): string | null {
  if (!s) return null;
  const trimmed = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [d, m, y] = trimmed.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const dd = String(parsed.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return null;
}

function isDescriptionRow(row: Record<string, string>): boolean {
  // Check if this looks like a description/instruction row rather than actual data
  const values = Object.values(row);
  const firstValue = (values[0] || "").toLowerCase();
  const allValues = values.join(" ").toLowerCase();
  
  return (
    firstValue.includes("nome empresarial") ||
    firstValue.includes("(obrigatório)") ||
    firstValue.includes("(opcional)") ||
    allValues.includes("(obrigatório)") ||
    allValues.includes("yyyy-mm-dd")
  );
}

function mapRowsToCustomerDtos(rows: Record<string, string>[]): CustomerDto[] {
  // Filter out description/instruction rows
  const dataRows = rows.filter(r => !isDescriptionRow(r));
  
  return dataRows.map((r) => {
    const name = r.name ?? r.nome ?? "";
    const email = r.email ?? "";
    const phone = r.phone ? phoneNumberHelper.cleanPhone(r.phone) : "";
    const companyDocument = r.companyDocument ?? r.document ?? r.cnpj ?? "";
    const postalBoxNumber = r.postalBoxNumber ?? r.endereco ?? r.caixa_postal ?? "";
    const legalRepresentativeName = r.legalRepresentativeName ?? r.representante ?? "";
    const legalRepresentativeDocument = r.legalRepresentativeDocument ?? r.representante_documento ?? "";
    const birthDateIso = toISODateFromPossibleFormats(r.birthDate ?? r.dataNascimento ?? r.data_nascimento ?? "");
    const address = r.address ?? r.endereco_cliente ?? r.enderecoCliente ?? "";
    const productName = (r.productName ?? r.productId ?? r.product ?? r.productName ?? "").toString();
    const affiliateId = r.affiliateId ?? r.affiliate_id ?? "";
    const startDateIso = toISODateFromPossibleFormats(r.startDate ?? r.contractStartDate ?? r["start_date"]);
    const endDateIso = toISODateFromPossibleFormats(r.endDate ?? r.contractEndDate ?? r["end_date"]);

    return {
      name,
      email,
      phone,
      companyDocument: companyDocument ? documentHelper.cleanDocument(companyDocument) : "",
      postalBoxNumber,
      legalRepresentativeName,
      legalRepresentativeDocument: legalRepresentativeDocument ? documentHelper.cleanDocument(legalRepresentativeDocument) : "",
      birthDate: birthDateIso || undefined,
      address: address || undefined,
      productName,
      affiliateId: affiliateId || undefined,
      startDate: startDateIso ?? "",
      endDate: endDateIso ?? "",
    };
  });
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(String(reader.result || ""));
    reader.onerror = (e) => rej(e);
    reader.readAsText(file, "UTF-8");
  });
}

function cleanHeader(h: string): string {
  if (!h) return h;
  let hh = h.trim();
  hh = hh.replace(/^['"]+|['"]+$/g, ""); // remove quotes externas
  hh = hh.replace(/,+$/g, ""); // remove vírgulas no fim
  hh = hh.replace(/\s+/g, " "); // colapsa espaços
  const low = hh.toLowerCase().replace(/\s+/g, "");
  // Mapeamento completo para todos os campos do exemplo
  if (low === "name" || low === "nome") return "name";
  if (low === "email") return "email";
  if (low === "phone" || low === "telefone") return "phone";
  if (low === "companydocument" || low === "document" || low === "cnpj") return "companyDocument";
  if (low === "postalboxnumber" || low === "caixapostal" || low === "endereco") return "postalBoxNumber";
  if (low === "legalrepresentativename" || low === "representante" || low === "nomedorepresentante") return "legalRepresentativeName";
  if (low === "legalrepresentativedocument" || low === "representantedocumento" || low === "cpfrepresentante") return "legalRepresentativeDocument";
  if (low === "birthdate" || low === "datanascimento" || low === "data_nascimento" || low === "datadenascimento") return "birthDate";
  if (low === "address" || low === "endereco_cliente" || low === "enderecocliente") return "address";
  if (low === "productid" || low === "produto" || low === "product" || low === "productname") return "productName";
  if (low === "affiliateid" || low === "indicacao" || low === "codigoindicacao") return "affiliateId";
  if (low === "startdate" || low === "datainicio" || low === "contractstartdate") return "startDate";
  if (low === "enddate" || low === "datafim" || low === "contractenddate") return "endDate";
  // outros campos genéricos
  if (low === "password" || low === "senha") return "password";
  if (low === "status") return "status";
  return hh; // fallback: retorna header "limpo"
}


  return (
    <>
      <button className={styles.addButton} onClick={handleFileUpload} disabled={parsing || loading}>
        <Plus size={16} /> {parsing ? 'Lendo arquivo...' : 'Upload Clientes'}
      </button>
      <input
        type="file"
        id="importFile"
        accept=".csv,.xlsx,.xls"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      
      {/* Preview Modal */}
      <ImportPreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreview}
        onConfirm={submitImport}
        data={previewData}
        loading={loading}
        fileName={fileName}
      />
      <ImportErrorModal
        isOpen={errorModalOpen}
        onClose={handleCloseErrorModal}
        errorDetails={errorDetails}
        fileName={fileName}
      />
    </>
  );
};

export default CsvUploadArea;
